/**
 * Video Loading Queue
 *
 * Priority queue that manages video loading order to prevent mobile crashes
 * on pages with many images. Limits concurrent video operations and unloads
 * videos that scroll far away from viewport.
 */

/**
 * State of a queued video
 */
export type QueuedVideoState = 'queued' | 'loading' | 'ready' | 'error';

/**
 * Queued video item
 */
export interface QueuedVideo {
    /** Unique image ID */
    imageId: string;
    /** Target element */
    element: HTMLElement;
    /** Image URL for video generation */
    imageUrl: string;
    /** Priority score - lower = higher priority (distance from viewport center) */
    priority: number;
    /** Current state */
    state: QueuedVideoState;
    /** Timestamp when queued */
    queuedAt: number;
}

/**
 * Queue configuration
 */
export interface VideoLoadingQueueConfig {
    /** Maximum concurrent video loads */
    maxConcurrent: number;
    /** Callback to start video loading */
    onStartLoad: (video: QueuedVideo) => Promise<void>;
    /** Callback when video should be unloaded */
    onUnload: (imageId: string) => void;
    /** Enable debug logging */
    debug?: boolean;
}

/**
 * Queue statistics
 */
export interface QueueStats {
    queued: number;
    loading: number;
    ready: number;
    error: number;
    total: number;
}

/**
 * VideoLoadingQueue class
 *
 * Manages a priority queue of videos to load, respecting concurrency limits.
 */
export class VideoLoadingQueue {
    private queue: Map<string, QueuedVideo> = new Map();
    private config: VideoLoadingQueueConfig;
    private isProcessing: boolean = false;

    constructor(config: VideoLoadingQueueConfig) {
        this.config = config;
    }

    /**
     * Add or update an image in the queue
     */
    enqueue(imageId: string, element: HTMLElement, imageUrl: string, priority: number): void {
        const existing = this.queue.get(imageId);

        if (existing) {
            // Update priority if already queued
            if (existing.state === 'queued') {
                existing.priority = priority;
                this.log(`📊 Updated priority: ${imageId} -> ${priority.toFixed(0)}`);
            }
            return;
        }

        // Add new item to queue
        const video: QueuedVideo = {
            imageId,
            element,
            imageUrl,
            priority,
            state: 'queued',
            queuedAt: Date.now(),
        };

        this.queue.set(imageId, video);
        this.log(`➕ Enqueued: ${imageId} (priority: ${priority.toFixed(0)})`);

        // Process queue
        this.processQueue();
    }

    /**
     * Remove image from queue and trigger unload
     */
    remove(imageId: string): void {
        const video = this.queue.get(imageId);
        if (!video) return;

        // Only unload if video was actually loaded
        if (video.state === 'ready' || video.state === 'loading') {
            this.config.onUnload(imageId);
        }

        this.queue.delete(imageId);
        this.log(`➖ Removed: ${imageId}`);

        // Process queue in case slot opened up
        this.processQueue();
    }

    /**
     * Update priority for an image (called on scroll)
     */
    updatePriority(imageId: string, priority: number): void {
        const video = this.queue.get(imageId);
        if (video && video.state === 'queued') {
            video.priority = priority;
        }
    }

    /**
     * Batch update priorities for all queued items
     */
    updateAllPriorities(calculatePriority: (element: HTMLElement) => number): void {
        for (const video of this.queue.values()) {
            if (video.state === 'queued') {
                video.priority = calculatePriority(video.element);
            }
        }
        // Re-process queue with new priorities
        this.processQueue();
    }

    /**
     * Mark video as ready (loading complete)
     */
    markReady(imageId: string): void {
        const video = this.queue.get(imageId);
        if (video) {
            video.state = 'ready';
            this.log(`✅ Ready: ${imageId}`);
            // Process queue in case slot opened up
            this.processQueue();
        }
    }

    /**
     * Mark video as error (loading failed)
     */
    markError(imageId: string): void {
        const video = this.queue.get(imageId);
        if (video) {
            video.state = 'error';
            this.log(`❌ Error: ${imageId}`);
            // Process queue in case slot opened up
            this.processQueue();
        }
    }

    /**
     * Check if an image is in the queue
     */
    has(imageId: string): boolean {
        return this.queue.has(imageId);
    }

    /**
     * Get state of a queued image
     */
    getState(imageId: string): QueuedVideoState | null {
        return this.queue.get(imageId)?.state ?? null;
    }

    /**
     * Get current queue statistics
     */
    getStats(): QueueStats {
        let queued = 0;
        let loading = 0;
        let ready = 0;
        let error = 0;

        for (const video of this.queue.values()) {
            switch (video.state) {
                case 'queued':
                    queued++;
                    break;
                case 'loading':
                    loading++;
                    break;
                case 'ready':
                    ready++;
                    break;
                case 'error':
                    error++;
                    break;
            }
        }

        return { queued, loading, ready, error, total: this.queue.size };
    }

    /**
     * Process the queue - start loading next items if under limit
     */
    private async processQueue(): Promise<void> {
        // Prevent concurrent processing
        if (this.isProcessing) return;
        this.isProcessing = true;

        try {
            const stats = this.getStats();

            // Check if we can start more loads
            while (stats.loading < this.config.maxConcurrent) {
                // Get next queued item by priority
                const next = this.getNextQueued();
                if (!next) break;

                // Start loading
                next.state = 'loading';
                this.log(`🎬 Loading: ${next.imageId} (${stats.loading + 1}/${this.config.maxConcurrent})`);

                // Call load handler (don't await - let it run in background)
                this.config.onStartLoad(next).catch(() => {
                    this.markError(next.imageId);
                });

                // Update stats for next iteration
                stats.loading++;
            }
        } finally {
            this.isProcessing = false;
        }
    }

    /**
     * Get next queued item with highest priority (lowest score)
     */
    private getNextQueued(): QueuedVideo | null {
        let best: QueuedVideo | null = null;

        for (const video of this.queue.values()) {
            if (video.state !== 'queued') continue;

            if (!best || video.priority < best.priority) {
                best = video;
            }
        }

        return best;
    }

    /**
     * Clear all items from queue
     */
    clear(): void {
        for (const imageId of this.queue.keys()) {
            this.remove(imageId);
        }
        this.log(`🧹 Queue cleared`);
    }

    /**
     * Debug logging
     */
    private log(message: string): void {
        if (this.config.debug) {
            const stats = this.getStats();
            console.log(`[VideoQueue] ${message} | Q:${stats.queued} L:${stats.loading} R:${stats.ready}`);
        }
    }
}
